import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { fetchWithCache } from '../lib/fetchWithCache';
import { SupplyShedDriveTime } from '../lib/types';
import perTreeSierra60 from '../lib/stubs/per-tree-rollup-california-sierra-nevada-60.json';

// Per-tree carbon rollup — backed by an S3 JSON file precomputed by
// scripts/generate-per-crown-data.py. When the file isn't available (other
// sheds, or before the script first runs) the hook falls back to a calibrated
// stub for `california-sierra-nevada:60` and surfaces `source: 'stub'`.
//
// IMPORTANT — labelling per the v1.2 data eng caveats:
//  - `totalTreeBiomassMg` = sum(AGB_kg) / 1000. INCLUDES coarse-root biomass
//    (~18% of total). UI must label this "Total Tree Biomass", never "AGB".
//  - `aboveGroundBiomassMg` = sum(AGB_kg - root_kg) / 1000. The AGB-only
//    figure to compare against the top-down satellite AGB.
//  - `treeCount` excludes fit_level=5 crowns (non-forest spatial fallback);
//    `treeCountTotal` is the unfiltered count.

export interface DiameterClassPoint {
  label: string;
  count: number;
}

export interface HeightBinPoint {
  label: string;
  count: number;
}

export interface PerTreeRollup {
  treeCount: number;
  treeCountTotal: number;
  sawtimberCount: number;
  totalTreeBiomassMg: number;
  aboveGroundBiomassMg: number;
  // null when the precompute couldn't run a meaningful comparison — most
  // commonly because v1.2 NAIP coverage is too partial for the AGB
  // shed-wide-average × covered-acres integral to be honest. The card hides
  // the comparison block in that case rather than render a misleading
  // agreement number.
  topDownAgbMg: number | null;
  agreementPct: number;
  coveredAcres: number;
  shedTotalAcres: number;
  diameterClasses: DiameterClassPoint[];
  heightBins: HeightBinPoint[];
  source: 'live' | 'stub';
}

interface Result {
  data: PerTreeRollup | null;
  isLoading: boolean;
  error: string | null;
}

const STUB_REGISTRY: Record<string, PerTreeRollup> = {
  'california-sierra-nevada:60': {
    treeCount: perTreeSierra60.treeCount,
    treeCountTotal: perTreeSierra60.treeCountTotal,
    sawtimberCount: perTreeSierra60.sawtimberCount,
    totalTreeBiomassMg: perTreeSierra60.totalTreeBiomassMg,
    aboveGroundBiomassMg: perTreeSierra60.aboveGroundBiomassMg,
    topDownAgbMg: perTreeSierra60.topDownAgbMg ?? null,
    agreementPct: perTreeSierra60.agreementPct,
    coveredAcres: perTreeSierra60.coveredAcres ?? 0,
    shedTotalAcres: perTreeSierra60.shedTotalAcres ?? 0,
    diameterClasses: perTreeSierra60.diameterClasses,
    heightBins: perTreeSierra60.heightBins,
    source: 'stub',
  },
};

const stubFor = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
): PerTreeRollup | null => {
  if (!millCode) return null;
  return STUB_REGISTRY[`${millCode}:${driveTime}`] ?? null;
};

const num = (v: unknown, fallback: number = 0): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const str = (v: unknown, fallback: string = ''): string =>
  typeof v === 'string' ? v : fallback;

const parseHistogramBins = <T extends DiameterClassPoint | HeightBinPoint>(
  raw: unknown,
): T[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const e = entry as Record<string, unknown>;
      const label = str(e.label);
      if (!label) return null;
      return { label, count: Math.round(num(e.count)) } as T;
    })
    .filter((entry): entry is T => entry !== null);
};

const parseRollup = (raw: unknown): PerTreeRollup | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const totalTreeBiomassMg = num(r.totalTreeBiomassMg);
  const aboveGroundBiomassMg = num(r.aboveGroundBiomassMg);
  // Server writes `null` (or omits) when partial-coverage skips the
  // comparison; treat 0 the same way (legacy precompute outputs).
  const rawTopDown = r.topDownAgbMg;
  const topDownAgbMg =
    rawTopDown === null || rawTopDown === undefined
      ? null
      : (() => {
          const n = num(rawTopDown);
          return n > 0 ? n : null;
        })();
  const treeCount = Math.round(num(r.treeCount));
  if (treeCount === 0 && totalTreeBiomassMg === 0) return null;
  const agreementPct =
    topDownAgbMg !== null && topDownAgbMg > 0
      ? (aboveGroundBiomassMg / topDownAgbMg) * 100
      : 0;
  return {
    treeCount,
    treeCountTotal: Math.round(num(r.treeCountTotal, treeCount)),
    sawtimberCount: Math.round(num(r.sawtimberCount)),
    totalTreeBiomassMg,
    aboveGroundBiomassMg,
    topDownAgbMg,
    agreementPct: Number.isFinite(agreementPct) ? agreementPct : 0,
    coveredAcres: num(r.coveredAcres),
    shedTotalAcres: num(r.shedTotalAcres),
    diameterClasses: parseHistogramBins<DiameterClassPoint>(r.diameterClasses),
    heightBins: parseHistogramBins<HeightBinPoint>(r.heightBins),
    source: 'live',
  };
};

export const usePerTreeRollup = (
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
  enabled: boolean = true,
): Result => {
  const [data, setData] = useState<PerTreeRollup | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.perCrownInsight(millCode, driveTime)}`;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache(url, (r) => r.json().then(parseRollup))
      .then((parsed) => {
        if (cancelled) return;
        if (parsed) {
          setData(parsed);
          setIsLoading(false);
          return;
        }
        const stub = stubFor(millCode, driveTime);
        setData(stub);
        setIsLoading(false);
        setError(stub ? null : 'Per-tree rollup not yet available for this shed.');
      })
      .catch(() => {
        if (cancelled) return;
        const stub = stubFor(millCode, driveTime);
        if (stub) {
          setData(stub);
          setIsLoading(false);
        } else {
          setData(null);
          setError('Per-tree rollup not yet available for this shed.');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTime, enabled]);

  return { data, isLoading, error };
};
